/**
 * Payment channel for audit — how money moved (cash drawer vs company bank).
 * Supports full cash, full bank, or a cash+bank split.
 */
export type PaymentVia = 'cash' | 'online' | 'split';

/** Company bank account configured in Settings → Banks. */
export type CompanyBankAccount = {
  id: string;
  bankName: string;
  accountName: string;
  accountNumber: string;
  ifsc: string;
  branch: string;
};

/** Stored on loan origination (finance / hand loan). */
export type DisbursementPayment = {
  disbursementVia?: PaymentVia | '';
  disbursementBankId?: string;
  /** Denormalized label for reports when bank catalog changes. */
  disbursementBankLabel?: string;
  disbursementPaidBy?: string;
  disbursementCashAmount?: number;
  disbursementBankAmount?: number;
};

/** Stored on EMI / collection / consultancy money rows. */
export type CollectionPayment = {
  paymentVia?: PaymentVia | '';
  companyBankId?: string;
  companyBankLabel?: string;
  paidBy?: string;
  cashAmount?: number;
  bankAmount?: number;
};

export function bankAccountLabel(bank: CompanyBankAccount): string {
  const parts = [bank.bankName, bank.branch].filter(Boolean);
  const tail = bank.accountNumber ? ` ••••${bank.accountNumber.slice(-4)}` : '';
  return `${parts.join(' — ')}${tail}`;
}

export function resolveBankLabel(banks: CompanyBankAccount[], bankId?: string, fallback = ''): string {
  if (!bankId) return fallback;
  const hit = banks.find((b) => b.id === bankId);
  return hit ? bankAccountLabel(hit) : fallback;
}

function parseMoney(v: unknown): number {
  const n = parseFloat(String(v ?? '').replace(/,/g, ''));
  return Number.isFinite(n) ? Math.max(0, n) : 0;
}

/**
 * Resolve how much of `total` hit cash vs bank for dashboard / audit.
 * Legacy rows (no split fields): online → all bank, else all cash.
 */
export function resolveCashBankAmounts(
  via: PaymentVia | '' | undefined,
  total: number,
  cashAmount?: number | string,
  bankAmount?: number | string
): { cash: number; bank: number } {
  const t = Math.max(0, Number(total) || 0);
  if (via === 'online') return { cash: 0, bank: t };
  if (via === 'split') {
    return { cash: parseMoney(cashAmount), bank: parseMoney(bankAmount) };
  }
  return { cash: t, bank: 0 };
}

/** Human label for payment history lists. */
export function paymentViaDisplayLabel(
  via: PaymentVia | '' | undefined,
  bankLabel?: string,
  cashAmount?: number,
  bankAmount?: number
): string {
  if (via === 'online') return bankLabel ? `Bank · ${bankLabel}` : 'Bank';
  if (via === 'cash') return 'Cash';
  if (via === 'split') {
    const parts = [
      cashAmount != null && cashAmount > 0 ? `Cash ₹${Math.round(cashAmount).toLocaleString('en-IN')}` : null,
      bankAmount != null && bankAmount > 0
        ? `Bank ₹${Math.round(bankAmount).toLocaleString('en-IN')}${bankLabel ? ` (${bankLabel})` : ''}`
        : null,
    ].filter(Boolean);
    return parts.length ? parts.join(' + ') : 'Cash + Bank';
  }
  return '—';
}
